import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconCheck, IconClose, IconPlus } from '../../UI';
import { DependencyTargetOption } from './DependencyTargetOption';
import { FloatingPopover } from './FloatingPopover';
import { InstanceBadge } from './InstanceBadge';
import { PortalSelect } from './PortalSelect';
import { DEP_LABEL_KEYS, DEP_OPTIONS } from './constants';
import { disabledDepTypes, validateNewDep } from './validation';
import type { EditorNode } from './types';
import styles from '../InterventionGraphEditor.module.css';
import { PlacementRuleType } from '../../../api/generated';

interface DependencyListProps {
  wi: EditorNode;
  allWeekInterventions: EditorNode[];
  getWiName: (wiId: string) => string;
  getWiLabel: (wiId: string) => string;
  isOverlay: boolean;
  strokeColor: string;
  onRemoveDep: (depId: string) => void;
  onAddDep: (type: PlacementRuleType, targetId?: string) => void;
}

export const DependencyList = ({
  wi,
  allWeekInterventions,
  getWiName,
  getWiLabel,
  isOverlay,
  strokeColor,
  onRemoveDep,
  onAddDep
}: DependencyListProps) => {
  const { t } = useTranslation();
  const addBtnRef = useRef<HTMLButtonElement>(null);
  const [adding, setAdding] = useState(false);
  const [addBtnRect, setAddBtnRect] = useState<DOMRect | null>(null);
  const [newType, setNewType] = useState<PlacementRuleType | ''>('');
  const [newTargetId, setNewTargetId] = useState('');
  const [errorKey, setErrorKey] = useState('');

  const needsTarget =
    newType && newType !== PlacementRuleType.First && newType !== PlacementRuleType.Last;

  const handleConfirm = () => {
    if (!newType) return;
    if (needsTarget && !newTargetId) {
      setErrorKey('config-dep-select-target-error');
      return;
    }
    const errKey = validateNewDep(
      allWeekInterventions,
      wi.id,
      newType,
      needsTarget ? newTargetId : undefined
    );
    if (errKey) {
      setErrorKey(errKey);
      return;
    }
    onAddDep(newType, needsTarget ? newTargetId : undefined);
    setAdding(false);
    setNewType('');
    setNewTargetId('');
    setErrorKey('');
  };

  const handleCancel = () => {
    setAdding(false);
    setNewType('');
    setNewTargetId('');
    setErrorKey('');
  };

  const typeDisabledKeys = disabledDepTypes(allWeekInterventions, wi.id);
  const targetOptions = allWeekInterventions.filter((o) => o.id !== wi.id);
  const targetSelected = targetOptions.find((o) => o.id === newTargetId) ?? null;
  // Disable any target that would be invalid for the chosen type (notably one
  // that would create a cycle), so a loop can't be built through the dropdown.
  const targetDisabledKeys = new Set(
    needsTarget && newType
      ? targetOptions
          .filter((o) => validateNewDep(allWeekInterventions, wi.id, newType, o.id) !== null)
          .map((o) => o.id)
      : []
  );

  const actionButtons = (
    <>
      <button
        className={styles.depConfirmBtn}
        onClick={handleConfirm}
        disabled={!newType}
        title={t('config-days-save')}
        aria-label={t('config-days-save')}
      >
        <IconCheck width={14} height={14} />
      </button>
      <button
        className={styles.depCancelBtn}
        onClick={handleCancel}
        title={t('config-cancel')}
        aria-label={t('config-cancel')}
      >
        <IconClose width={14} height={14} />
      </button>
    </>
  );

  return (
    <div className={styles.depList}>
      {wi.dependencies.map((dep) => {
        const typeLabel = t(DEP_LABEL_KEYS[dep.type]);
        const targetName = dep.targetId ? getWiName(dep.targetId) : '';
        const targetLabel = dep.targetId ? getWiLabel(dep.targetId) : '';
        const depFullLabel = typeLabel + (dep.targetId ? `: ${targetLabel} ${targetName}` : '');
        return (
          <span
            key={dep.id}
            className={styles.depTag}
            style={{ borderColor: strokeColor }}
            title={depFullLabel}
          >
            <span className={styles.depTagLabel}>
              {typeLabel}
              {dep.targetId && (
                <>
                  {': '}
                  <InstanceBadge label={targetLabel} />
                  {` ${targetName}`}
                </>
              )}
            </span>
            {!isOverlay && (
              <button
                className={styles.depTagRemove}
                onClick={(e) => {
                  e.stopPropagation();
                  onRemoveDep(dep.id);
                }}
                title={t('config-dep-remove')}
                aria-label={t('config-dep-remove')}
              >
                <IconClose width={12} height={12} />
              </button>
            )}
          </span>
        );
      })}

      {!isOverlay && (
        <button
          ref={addBtnRef}
          className={`${styles.addDepBtn} ${adding ? styles.addDepBtnActive : ''}`}
          onClick={(e) => {
            e.stopPropagation();
            setAddBtnRect(addBtnRef.current?.getBoundingClientRect() ?? null);
            setAdding(true);
          }}
          style={{ color: strokeColor, borderColor: strokeColor }}
        >
          <IconPlus width={12} height={12} /> {t('config-dep-add')}
        </button>
      )}

      <FloatingPopover open={adding} triggerRect={addBtnRect} onClose={handleCancel} width={280}>
        <div className={styles.popoverBody}>
          <div className={styles.addDepForm} onClick={(e) => e.stopPropagation()}>
            <PortalSelect
              options={DEP_OPTIONS}
              selected={newType || null}
              getKey={(opt) => opt}
              renderOption={(opt) => t(DEP_LABEL_KEYS[opt])}
              onChange={(value) => {
                setNewType(value);
                setErrorKey('');
                setNewTargetId('');
              }}
              placeholder={t('config-dep-type-placeholder')}
              disabledKeys={typeDisabledKeys}
            />
            <PortalSelect
              options={targetOptions}
              selected={targetSelected}
              getKey={(o) => o.id}
              renderOption={(o) => (
                <DependencyTargetOption label={getWiLabel(o.id)} name={getWiName(o.id)} />
              )}
              onChange={(value) => {
                setNewTargetId(value.id);
                setErrorKey('');
              }}
              placeholder={t('config-dep-target-placeholder')}
              disabled={!needsTarget}
              disabledKeys={targetDisabledKeys}
            />
            {errorKey && <span className={styles.depError}>{t(errorKey)}</span>}
            <div className={styles.addDepActions}>{actionButtons}</div>
          </div>
        </div>
      </FloatingPopover>
    </div>
  );
};
